import { useEffect, useState, type ChangeEvent } from 'react'
import {
  api,
  type AccountMember,
  type InvitableAccount,
  type Membership,
  type RoleOption,
} from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { EntityForm, Field } from '../components/EntityForm.tsx'
import { Input } from '../components/Input.tsx'
import { Select } from '../components/Select.tsx'
import { icons } from '../components/icons.ts'
import { useConfirm } from '../components/Confirm.tsx'
import { useToast } from '../components/Toast.tsx'

/**
 * One person (ADR 0024): who they are, and in each pharmacy they belong to,
 * their função and their Formula Certa user.
 *
 * A pharmacy's manager sees and edits their own pharmacy's part. A super
 * admin sees every pharmacy the person is in, and adds them to another or
 * takes them out of one -- somebody invited into the wrong account is fixed
 * here rather than by another invite.
 */
export function User({ id, onBack }: { id: string; onBack: () => void }) {
  const [user, setUser] = useState<AccountMember | undefined>()
  const [roles, setRoles] = useState<RoleOption[]>([])
  const [accounts, setAccounts] = useState<InvitableAccount[]>([])
  const [isSelf, setIsSelf] = useState(false)
  const [error, setError] = useState<string | undefined>()

  function load() {
    void api
      .user(id)
      .then((r) => {
        setUser(r.user)
        setRoles(r.roles)
        setAccounts(r.accounts)
        setIsSelf(r.isSelf)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }

  useEffect(load, [id])

  // Only a super admin is sent the list of pharmacies.
  const everywhere = accounts.length > 0
  const elsewhere = accounts.filter(
    (account) => !user?.memberships.some((m) => m.account_id === account.id),
  )

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Usuários
        </Button>
        <h1 className="text-tH4 text-text-strong">{user?.name ?? user?.email ?? 'Usuário'}</h1>
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {user === undefined ? (
        error === undefined && <p className="text-tP text-text-weak">Carregando…</p>
      ) : (
        <>
          <EntityForm>
            <Field label="E-mail" value={user.email} />
            <Field label="Nome" value={user.name ?? ''} />
            <Field label="Situação" value={user.active ? 'Ativo' : 'Inativo'} />
            {/* Ours to grant, at the invite; shown so the funções below read right. */}
            <Field
              label="Super admin"
              value={user.system_role === 'super_admin' ? 'Sim — vê e faz tudo' : 'Não'}
            />
          </EntityForm>

          {user.memberships.map((membership) => {
            const theirs = roles.filter((role) => role.account_id === membership.account_id)
            // Nobody takes the system role off themselves (ADR 0024).
            const ownAdmin =
              isSelf && theirs.some((role) => role.id === membership.role_id && role.system)
            return (
              <MembershipForm
                key={membership.account_id}
                userId={user.user_id}
                membership={membership}
                roles={theirs}
                ownAdmin={ownAdmin}
                // The last pharmacy is never removed: deactivating is for that.
                removable={everywhere && user.memberships.length > 1 && !ownAdmin}
                onChanged={load}
              />
            )
          })}

          {everywhere && elsewhere.length > 0 && (
            <AddMembership
              userId={user.user_id}
              accounts={elsewhere}
              roles={roles}
              onAdded={load}
            />
          )}
        </>
      )}
    </div>
  )
}

function MembershipForm({
  userId,
  membership,
  roles,
  ownAdmin,
  removable,
  onChanged,
}: {
  userId: string
  membership: Membership
  roles: readonly RoleOption[]
  /** The session's own Admin membership, whose função it may not change. */
  ownAdmin: boolean
  removable: boolean
  onChanged: () => void
}) {
  const [roleId, setRoleId] = useState(membership.role_id ?? '')
  const [erpUser, setErpUser] = useState(membership.erp_user ?? '')
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()

  const dirty =
    roleId !== (membership.role_id ?? '') || erpUser.trim() !== (membership.erp_user ?? '')

  async function save() {
    setSaving(true)
    try {
      await api.updateUser(userId, {
        accountId: membership.account_id,
        roleId: roleId === '' ? null : roleId,
        erpUser: erpUser.trim() === '' ? null : erpUser.trim(),
      })
      toast.show('Usuário salvo', { tone: 'success' })
      onChanged()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    const sure = await confirm({
      title: `Tirar da ${membership.account_name}?`,
      message: 'A pessoa deixa de ver as conferências desta farmácia.',
      confirmLabel: 'Tirar',
      tone: 'danger',
    })
    if (!sure) return
    try {
      await api.removeMembership(userId, membership.account_id)
      toast.show('Removido da farmácia', { tone: 'success' })
      onChanged()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao remover', { tone: 'error' })
    }
  }

  return (
    <EntityForm
      title={
        <span className="flex items-center gap-3">
          {membership.account_name}
          {removable && (
            <Button
              variant="tertiary"
              tone="danger"
              size="small"
              leadingIcon={icons.remove}
              onClick={() => void remove()}
            >
              Tirar desta farmácia
            </Button>
          )}
        </span>
      }
      onSave={() => void save()}
      saving={saving}
      dirty={dirty}
    >
      <Select
        label="Função"
        value={roleId}
        disabled={saving || ownAdmin}
        helperText={
          ownAdmin
            ? 'Você não pode tirar a função Admin de si mesmo.'
            : 'Quais conferências esta pessoa enxerga nesta farmácia.'
        }
        onChange={(e: ChangeEvent<HTMLSelectElement>) => setRoleId(e.target.value)}
      >
        {/* Offered so it can be chosen, and named for what it does. */}
        <option value="">Sem função (não vê nada)</option>
        {roles.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
          </option>
        ))}
      </Select>
      <Input
        label="Usuário no Fórmula Certa"
        placeholder="7-1236"
        value={erpUser}
        disabled={saving}
        helperText="O par CDCON-CDFUN do cadastro de funcionários desta farmácia. É por ele que os orçamentos desta pessoa são reconhecidos como seus."
        onChange={(e) => setErpUser(e.target.value)}
      />
    </EntityForm>
  )
}

/** A super admin putting somebody in another pharmacy, with a função there. */
function AddMembership({
  userId,
  accounts,
  roles,
  onAdded,
}: {
  userId: string
  accounts: readonly InvitableAccount[]
  roles: readonly RoleOption[]
  onAdded: () => void
}) {
  const [accountId, setAccountId] = useState('')
  const [roleId, setRoleId] = useState('')
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const theirRoles = roles.filter((role) => role.account_id === accountId)

  async function add() {
    setSaving(true)
    try {
      await api.addMembership(userId, accountId, roleId)
      toast.show('Adicionado à farmácia', { tone: 'success' })
      setAccountId('')
      setRoleId('')
      onAdded()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao adicionar', { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <EntityForm
      title="Adicionar a uma farmácia"
      description="Vale na hora, sem convite: a pessoa passa a poder abrir esta farmácia pelo menu."
      onSave={() => void add()}
      saving={saving}
      dirty={accountId !== '' && roleId !== ''}
      saveLabel="Adicionar"
    >
      <Select
        label="Farmácia"
        value={accountId}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          setAccountId(e.target.value)
          // The função is that pharmacy's own, so choosing again starts over.
          setRoleId('')
        }}
      >
        <option value="" disabled>
          Escolha…
        </option>
        {accounts.map((account) => (
          <option key={account.id} value={account.id}>
            {account.name}
          </option>
        ))}
      </Select>
      <Select
        label="Função"
        value={roleId}
        disabled={accountId === ''}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => setRoleId(e.target.value)}
      >
        <option value="" disabled>
          {accountId === '' ? 'Escolha a farmácia primeiro' : 'Escolha…'}
        </option>
        {theirRoles.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
          </option>
        ))}
      </Select>
    </EntityForm>
  )
}
